import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch, type SubmitHandler } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useRemoveCustomerSchema } from "@features/RemoveCustomerModal/useRemoveCustomerSchema";
import { Button } from "@components/Button/Button";
import { CompanyEmailLink } from "@components/CompanyEmailLink/CompanyEmailLink";
import { CompanyPhoneLink } from "@components/CompanyPhoneLink/CompanyPhoneLink";
import { BinIcon, CrossIcon } from "@components/icons";
import { FormCheckbox } from "@components/inputs/form/FormCheckbox/FormCheckbox";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { FormTextarea } from "@components/inputs/form/FormTextarea/FormTextarea";
import { Modal } from "@components/Modal/Modal";
import { Notification } from "@components/Notification/Notification";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { useTranslationArray } from "@hooks/useTranslationArray";
import { type IUseModalProps } from "@store/modal/modal";
import { useRemoveCustomerMutation } from "@services/customer/mutations";
import {
  useCustomerDetailsQuery,
  useCustomerRemoveReasonsQuery,
} from "@services/customer/queries";
import { useOverviewQuery } from "@services/overview/queries";
import { clearUserState, isNullOrUndefined } from "@utils/common";
import { sanitizeHtml } from "@utils/html";

interface IFormData {
  agreed: boolean;
  customerStateChangingReasonId: number;
  comment: string;
}

export const RemoveCustomerModal: React.FC<IUseModalProps> = (props) => {
  const { t } = useTranslation();
  const { data: customerDetails } = useCustomerDetailsQuery();
  const outcome = useTranslationArray("removeCustomerModal.outcome", {
    email: customerDetails?.billForwardEmail,
  });

  const modal = useModal(props);
  const schema = useRemoveCustomerSchema();
  const { data: overview } = useOverviewQuery();
  const isRemoveAvailable = !overview?.jointBillServices?.find(
    (jbs) => !!jbs.projectTypeId,
  );
  const { data: reasons = [], isLoading: isReasonsLoading } =
    useCustomerRemoveReasonsQuery();
  const { mutateAsync: removeCustomer, isPending } =
    useRemoveCustomerMutation();
  const { handleSubmit, control, setError } = useForm<IFormData>({
    resolver: zodResolver(schema),
  });
  const { setApiErrors } = useApiErrorUtils(setError);

  const [reasonIdValue] = useWatch({
    name: ["customerStateChangingReasonId"],
    control,
  });

  const remove: SubmitHandler<IFormData> = async (data) => {
    const response = await removeCustomer({
      ...(data.customerStateChangingReasonId
        ? { customerStateChangingReasonId: data.customerStateChangingReasonId }
        : {}),
      comment: data.comment ?? "",
    }).catch(setApiErrors);

    if (!isNullOrUndefined(response)) {
      modal.close();
      clearUserState();
      toast.success(t("removeCustomerModal.toast.success"));
    }
  };

  const commentRequired = reasons.find(
    (reason) => reason.customerStateChangingReasonId === reasonIdValue,
  )?.isCommentRequired;

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: BinIcon, color: "red" }}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("removeCustomerModal.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            type="submit"
            isLoading={isPending}
            onClick={handleSubmit(remove)}
            disabled={!isRemoveAvailable}
          >
            {t("removeCustomerModal.buttons.submit")}
          </Button>
        </React.Fragment>
      }
      title={t("removeCustomerModal.title")}
      className="md:max-w-xl"
    >
      <div className="flex flex-col gap-3">
        <div className="text-sm text-grey-dark">
          <p>
            <Trans
              i18nKey="removeCustomerModal.description"
              components={{
                phone: <CompanyPhoneLink></CompanyPhoneLink>,
                email: <CompanyEmailLink></CompanyEmailLink>,
                br: <br />,
                b: <span className="font-semibold"></span>,
              }}
            ></Trans>
          </p>
          <Trans
            i18nKey="removeCustomerModal.info"
            components={{
              phone: <CompanyPhoneLink></CompanyPhoneLink>,
              email: <CompanyEmailLink></CompanyEmailLink>,
              b: <span className="font-semibold"></span>,
            }}
          />
        </div>
        <div className="flex flex-col gap-3">
          {outcome?.map((item) => (
            <div className="flex items-center gap-2">
              <CrossIcon className="size-4 min-w-4 text-base-red" />
              <span
                className="text-sm text-grey-dark"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(item) }}
              />
            </div>
          ))}
        </div>
        <form
          onSubmit={handleSubmit(remove)}
          className="flex flex-col gap-5 pt-2"
        >
          <FormSelect
            label={t("removeCustomerModal.form.fields.reasonId.label")}
            name="customerStateChangingReasonId"
            control={control}
            options={reasons.map(({ customerStateChangingReasonId }) => ({
              label: t(
                `removeCustomerModal.reasons.${customerStateChangingReasonId}`,
              ),
              value: customerStateChangingReasonId,
            }))}
            isLoading={isReasonsLoading}
          />
          {commentRequired && (
            <FormTextarea
              control={control}
              name="comment"
              placeholder={t(
                "removeCustomerModal.form.fields.comment.placeholder",
              )}
            />
          )}
          <FormCheckbox
            label={t("removeCustomerModal.form.fields.agreed.label")}
            name="agreed"
            control={control}
          />
        </form>
        {!isRemoveAvailable && (
          <Notification
            color="red"
            description={t("removeCustomerModal.unRemovableDescription")}
          />
        )}
      </div>
    </Modal>
  );
};
